import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import Button from '../../components/ui/Button';
import InputField from '../../components/ui/InputField';
import TextAreaField from '../../components/ui/TextAreaField';
import LeftArrowIcon from '../../components/icons/LeftArrowIcon';
import SaveIcon from '../../components/icons/SaveIcon';
import { toast } from 'react-toastify';
import { useLayoutContext } from '../../components/layout/Layout';
import { getMakecodeArcadePageRootStyle } from './makecodeArcadePageStyles';
import 'react-toastify/dist/ReactToastify.css';
import { parseImgTemplatePixelRows } from '../../utils/makecodeArcadeImg';

export const CodeEditor: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { isMobile } = useLayoutContext();
  const [spriteName, setSpriteName] = useState('');
  const [spriteCode, setSpriteCode] = useState('');
  const [spriteGenre, setSpriteGenre] = useState('');
  const [spriteSize, setSpriteSize] = useState({ width: 0, height: 0 });
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const genres = [
    { value: 'fantasy', label: '판타지' }, { value: 'horror', label: '공포' }, { value: 'medieval', label: '중세' },
    { value: 'scifi', label: 'SF' }, { value: 'modern', label: '현대' }, { value: 'cute', label: '귀여운' },
    { value: 'monster', label: '몬스터' }, { value: 'robot', label: '로봇' }, { value: 'nature', label: '자연' },
    { value: 'food', label: '음식' }, { value: 'other', label: '기타' }
  ];

  const palette = [
    [0, 0, 0, 0], [255, 255, 255], [255, 33, 33], [255, 147, 196], [255, 129, 53], [255, 246, 9],
    [36, 156, 163], [120, 220, 82], [0, 63, 173], [135, 242, 255], [142, 46, 196], [164, 131, 159],
    [92, 64, 108], [229, 205, 196], [145, 70, 61], [0, 0, 0]
  ];

  const updatePreview = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let imageData = spriteCode;
    const imgMatch = spriteCode.match(/img`([\s\S]*?)`/);
    if (imgMatch) imageData = imgMatch[1];
    const normalizedRows = parseImgTemplatePixelRows(imageData);
    if (normalizedRows.length === 0) return;
    const width = Math.max(...normalizedRows.map(row => row.length));
    const height = normalizedRows.length;
    setSpriteSize({ width, height });
    canvas.width = width;
    canvas.height = height;
    normalizedRows.forEach((row, y) => {
      for (let x = 0; x < width; x++) {
        const pixel = row[x] ?? '.';
        if (pixel === '.') ctx.clearRect(x, y, 1, 1);
        else {
          const colorIndex = parseInt(pixel, 16);
          if (colorIndex >= 0 && colorIndex < palette.length) {
            const color = palette[colorIndex];
            ctx.fillStyle = `rgb(${color[0]},${color[1]},${color[2]})`;
            ctx.fillRect(x, y, 1, 1);
          }
        }
      }
    });
    const scale = 4;
    canvas.style.width = `${width * scale}px`;
    canvas.style.height = `${height * scale}px`;
  };

  useEffect(() => { updatePreview(); }, [spriteCode]);

  const handleSave = async () => {
    if (!currentUser) { toast.info('로그인이 필요합니다.'); return; }
    if (!spriteName.trim()) { toast.info('스프라이트 이름을 입력해주세요.'); return; }
    if (!spriteCode.trim()) { toast.info('스프라이트 코드를 입력해주세요.'); return; }
    if (!spriteGenre) { toast.info('장르를 선택해주세요.'); return; }
    try {
      const canvas = canvasRef.current;
      if (!canvas) return;
      await addDoc(collection(db, 'sprites'), {
        name: spriteName, code: spriteCode, genre: spriteGenre,
        authorId: currentUser.uid, authorName: currentUser.email?.split('@')[0] || '익명',
        createdAt: serverTimestamp(), likes: [], previewUrl: canvas.toDataURL(),
        width: spriteSize.width, height: spriteSize.height
      });
      toast.success('스프라이트가 저장되었습니다.');
      navigate('/makecodearcade/sprite-gallery');
    } catch (error) {
      console.error('Error saving sprite:', error);
      toast.error('스프라이트 저장 중 오류가 발생했습니다.');
    }
  };

  return (
    <div style={getMakecodeArcadePageRootStyle(isMobile)}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ margin: 0, fontSize: '24px', fontFamily: 'var(--font-family-primary)', color: 'var(--Color-text-strong)' }}>코드로 스프라이트 등록</h1>
        <Button variant="outline" leftIcon={<LeftArrowIcon width={20} height={20} />} onClick={() => navigate('/makecodearcade/sprite-gallery')}>스프라이트 갤러리로</Button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div style={{ background: '#f5f5f5', padding: '20px', borderRadius: '8px', fontFamily: 'monospace', whiteSpace: 'pre-wrap', height: '400px', overflow: 'auto' }}>
            <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '8px', color: 'var(--Color-text-normal)' }}>스프라이트 코드 예시:</div>
            <pre style={{ margin: 0, fontSize: '12px' }}>{`let mySprite = sprites.create(img\`...\`, SpriteKind.Player)`}</pre>
          </div>
          <div style={{ fontSize: '14px', fontWeight: 500 }}>미리보기:</div>
          <canvas ref={canvasRef} style={{ width: '100%', aspectRatio: '1', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: '8px', imageRendering: 'pixelated' }} />
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', padding: '8px', background: '#f5f5f5', borderRadius: '4px', fontSize: '14px', color: '#666' }}>
            픽셀 크기: {spriteSize.width} × {spriteSize.height}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <InputField label="스프라이트 이름" value={spriteName} onChange={setSpriteName} width="100%" />
          <div>
            <label style={{ fontSize: '14px', fontWeight: 500, color: '#161616', marginBottom: '8px', display: 'block' }}>장르</label>
            <select value={spriteGenre} onChange={(e) => setSpriteGenre(e.target.value)} style={{ width: '100%', height: '48px', padding: '12px 16px', border: '1.2px solid #F0F0F0', borderRadius: '8px', fontSize: '14px', fontFamily: 'var(--font-family-primary)', backgroundColor: 'white' }}>
              <option value="">선택</option>
              {genres.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </div>
          <TextAreaField label="스프라이트 코드" value={spriteCode} onChange={setSpriteCode} width="100%" rows={10} placeholder="스프라이트 코드를 입력하세요..." />
          <Button variant="primary" leftIcon={<SaveIcon width={20} height={20} />} onClick={handleSave} width="100%">저장하기</Button>
        </div>
      </div>
    </div>
  );
};
